import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Box from "@mui/material/Box";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { axiosReqConfig } from "../functions/axiosRequest";
import { API_IP, STRIPE_KEY } from "../global_variables";
import { format, getDay, set } from "date-fns";
import { enGB, is } from "date-fns/locale";
import PaymentModal from "./AccountComponents/PaymentModal";

const BookingScreen = () => {
  const [activeTab, setActiveTab] = useState("Frequently Asked Questions"); // Default tab
  const [bookings, setBookings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(7);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [customerSession, setCustomerSession] = useState("");
  const [selectedBooking, setSelectedBooking] = useState({});
  const [refresh, isRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const stripePromise = loadStripe(STRIPE_KEY);
  const togglePayment = () => {
    setPaymentVisible(!paymentVisible);
  };
  const toggleRefresh = () => {
    isRefresh(!refresh);
  };

  const totalItems = bookings.length;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = bookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const steps = [
    "Booking",
    "Wait for Response (Provider)",
    "Payment",
    "Completion",
  ];
  const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(255, 235, 59) 0%, rgb(255, 204, 0) 50%, rgb(230, 200, 0) 50%)",
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundImage:
          "linear-gradient(136deg, rgb(200, 255, 200) 0%, rgb(144, 238, 144) 50%, rgb(46, 139, 87) 100%)",
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor: "#eaeaf0",
      borderRadius: 1,
      ...theme.applyStyles("dark", {
        backgroundColor: theme.palette.grey[800],
      }),
    },
  }));
  const ColorlibStepIconRoot = styled("div")(({ theme }) => ({
    backgroundColor: "#ccc",
    zIndex: 1,
    color: "#fff",
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[700],
    }),
    variants: [
      {
        props: ({ ownerState }) => ownerState.active,
        style: {
          backgroundImage: "linear-gradient(rgb(230, 200, 0) 50%)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
      {
        props: ({ ownerState }) => ownerState.completed,
        style: {
          backgroundImage: "linear-gradient(rgb(46, 139, 87) 100%)",
          boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
        },
      },
    ],
  }));

  function ColorlibStepIcon(props) {
    const { active, completed, className } = props;

    const icons = {
      1: (
        <img
          src={require(`../images/Booking-icon.png`)}
          className="BookingIcon"
        />
      ),
      2: (
        <img src={require(`../images/wait-icon.png`)} className="BookingIcon" />
      ),
      3: (
        <img
          src={require(`../images/payment-icon.png`)}
          className="BookingIcon"
        />
      ),
      4: (
        <img
          src={require(`../images/completion-icon.png`)}
          className="BookingIcon"
        />
      ),
    };

    return (
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    );
  }

  const fetchBookingInfo = useCallback(async () => {
    try {
      const data = await axiosReqConfig(`${API_IP}/api/dashboard`, "get", {});

      console.log("Fetched data:", data);

      setBookings(data.bookings);
      console.log("Bookings:", data.bookings);
      return data;
    } catch (error) {
      console.error("Error fetching booking info:", error);

      return null;
    }
  }, [refresh]);

  function formatDateWithDay(dateString) {
    const date = new Date(dateString);

    // Format the date (e.g., "12 December 2024")
    const formattedDate = format(date, "d MMMM yyyy", { locale: enGB });

    // Get the day of the week (e.g., "Thu")
    const dayOfWeek = format(date, "EEE", { locale: enGB });

    return `${dayOfWeek} ${formattedDate}`;
  }

  async function requestPayment(booking_id, service_id, provider_id) {
    setIsLoading(true);
    try {
      const data = await axiosReqConfig(`${API_IP}/api/payment`, "post", {
        booking_id: booking_id,
        service_id: service_id,
        provider_id: provider_id,
      });

      const {
        clientSecret,
        totalPrice,
        transactionId,
        ephemeralKey,
        customer,
        customerSession,
      } = data;
      setSelectedBooking({
        transactionId: transactionId,
        booking_id: booking_id,
        provider_id: provider_id,
      });
      setTotalPrice(totalPrice);
      setCustomerSession(customerSession);
      setClientSecret(clientSecret);
      setPaymentVisible(true);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(console.error());
    }
  }

  const BookingInfoDiv = ({ booking }) => {
    const [isFooterExpand, setIsFooterExpand] = useState(false);
    const toggleFooterExpand = () => {
      setIsFooterExpand(!isFooterExpand);
    };
    const start_date = formatDateWithDay(booking.start_date);
    let currentStep = 0;
    if (!booking.provider_confirmation && !booking.paid) {
      currentStep = 1;
    } else if (!booking.paid) {
      currentStep = 2;
    } else if (booking.paid && booking.acceptance === true) {
      currentStep = 4;
    }
    console.log("Current Step:", booking.acceptance);
    return (
      <div className="BookingContainer">
        <div className="BookingHeader">
          <div className="BookingHeaderLeft">
            <img
              src={require(`../images/provider-icon.png`)}
              className="BookingProviderIcon"
            />
            <h4>
              {booking.provider_name} -{" "}
              {booking.acceptance === null ? (
                <span className="booking_pending_span">
                  Pending{" "}
                  <img
                    className="booking_alert_icon"
                    alt="Booking Pending Icon"
                    src={require("../images/booking_pending_icon.png")}
                  />{" "}
                </span>
              ) : (
                <>
                  {!booking.acceptance ? (
                    <span className="booking_denied_span">
                      Declined{" "}
                      <img
                        className="booking_alert_icon"
                        alt="Booking Pending Icon"
                        src={require("../images/booking_denied_icon.png")}
                      />
                    </span>
                  ) : (
                    <span className="booking_accepted_span">
                      Accepted{" "}
                      <img
                        className="booking_alert_icon"
                        alt="Booking Pending Icon"
                        src={require("../images/booking_accepted_icon.webp")}
                      />
                    </span>
                  )}
                </>
              )}
            </h4>
          </div>
          <div className="BookingHeaderRight">
            <p>ID: {booking.booking_id}</p>
          </div>
        </div>
        <div className="BookingBody">
          <div className="BookingTime">
            <p>Start Date</p>
            <h2>{start_date}</h2>
            <p>
              Service: <span>{booking.care_service_name}</span>
            </p>
          </div>
          <div className="BookingProcess">
            <Stack sx={{ width: "100%" }} spacing={4}>
              <Stepper
                alternativeLabel
                activeStep={currentStep}
                connector={<ColorlibConnector />}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel StepIconComponent={ColorlibStepIcon}>
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Stack>
          </div>
          <div className="BookingPatientDetail">
            <p>Member</p>
            <h3>
              <span>{`${booking.first_name} ${booking.last_name}`}</span>
            </h3>
            <p>
              Location: <span>Victoria</span>
            </p>
          </div>
        </div>
        <div className="BookingFooter">
          <div className="BookingFooterMoreOptions">
            <button
              onClick={toggleFooterExpand}
              className="BookingMoreDetailsButton"
            >
              More Details
            </button>
            {currentStep == 1 ? (
              <>
                {booking.acceptance === null ? (
                  <>
                    <button className="BookingWaitButton">
                      Wait For Provider
                    </button>
                  </>
                ) : (
                  <>
                    <button className="BookingWaitButton">
                      Provider Has Declined
                    </button>
                  </>
                )}
              </>
            ) : (
              <></>
            )}
            {currentStep == 2 ? (
              <button
                onClick={() =>
                  requestPayment(
                    booking.booking_id,
                    booking.care_service_id,
                    booking.provider_id,
                    booking.total_hours
                  )
                }
                className="BookingPayButton"
                disabled={isLoading}
              >
                Pay To Process
              </button>
            ) : (
              <></>
            )}
          </div>
        </div>
        {isFooterExpand ? (
          <>
            <div className="BookingFooterExpand">
              <h5>
                Here are dates that the provider has{" "}
                <span className="booking_accepted_span">accepted</span>
              </h5>
              {booking.accepted_dates.map((date, index) => {
                const formatDate = formatDateWithDay(date);
                return (
                  <p>
                    <img
                      src={require(`../images/date-confirmed.png`)}
                      className="date-icon"
                    />
                    {formatDate} ({booking.accepted_start_times[index]} -{" "}
                    {booking.accepted_end_times[index]})
                  </p>
                );
              })}
            </div>
            <div className="BookingFooterExpand">
              <h5>
                Here are dates that the provider has{" "}
                <span className="booking_denied_span">declined</span>
              </h5>
              {booking.declined_dates.map((date) => {
                if (booking.acceptance !== null) {
                  const formatDate = formatDateWithDay(date);
                  return (
                    <p>
                      <img
                        src={require(`../images/date-confirmed.png`)}
                        className="date-icon"
                      />
                      {formatDate}
                    </p>
                  );
                } else {
                  return <></>;
                }
              })}
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchBookingInfo();
  }, [fetchBookingInfo]);
  return (
    <>
      {paymentVisible ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            customerSessionClientSecret: customerSession,
          }}
        >
          <PaymentModal
            showModal={paymentVisible}
            handleClose={togglePayment}
            clientSecret={clientSecret}
            customerSession={customerSession}
            totalPrice={totalPrice}
            selectedBooking={selectedBooking}
            toggleRefresh={toggleRefresh}
          />
        </Elements>
      ) : (
        <></>
      )}
      <header className="titleContainer">
        <h1>Booking</h1>
      </header>
      <div className="bookingHistoryContainer">
        <div className="bookingHistoryOptions">
          <div className="bookingHistoryOptionsLeft">
            <button className="bookingOptionsButton bookingOptionsProcess">
              In Process
            </button>
            <button className="bookingOptionsButton bookingOptionsCompleted">
              Completed
            </button>
            <button className="bookingOptionsButton">
              {bookings.length} requests
            </button>
          </div>

          <div className="bookingPagination bookingHistoryOptionsRight">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="bookingPaginationButton"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`bookingPaginationButton ${
                    currentPage === pageNumber
                      ? "bookingPaginationButtonActive"
                      : ""
                  }`}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="bookingPaginationButton"
            >
              Next
            </button>
          </div>
        </div>
        <div className="BookingListContainer">
          {currentItems.map((booking) => (
            <BookingInfoDiv key={booking.id} booking={booking} />
          ))}
        </div>
        <div className="bookingPagination bookingPaginationBottomRight">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="bookingPaginationButton"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(
            (pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={`bookingPaginationButton ${
                  currentPage === pageNumber
                    ? "bookingPaginationButtonActive"
                    : ""
                }`}
              >
                {pageNumber}
              </button>
            )
          )}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="bookingPaginationButton"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default BookingScreen;
